import { useState, useEffect, useRef } from 'react'
import { supabase } from '@/integrations/supabase/client'
import { ChevronDown } from 'lucide-react'

// Module-level cache — persists for the session
const cache: Record<string, string[]> = {}

export async function loadDistinctValues(column: string): Promise<string[]> {
    if (cache[column]) return cache[column]
    const { data } = await supabase.from('beneficiaries').select(column).not(column, 'is', null)
    if (!data) return []
    const distinct = [...new Set((data as any[]).map(r => r[column]).filter(Boolean))].sort() as string[]
    cache[column] = distinct
    return distinct
}

export function invalidateDistinctCache(column: string) {
    delete cache[column]
}

interface SmartComboboxProps {
    column: string
    value: string
    onChange: (v: string) => void
    isAdmin: boolean
    inputClassName?: string
    autoFocus?: boolean
    onKeyDown?: (e: React.KeyboardEvent) => void
}

export const SmartCombobox = ({
    column,
    value,
    onChange,
    isAdmin,
    inputClassName = '',
    autoFocus,
    onKeyDown,
}: SmartComboboxProps) => {
    const [options, setOptions] = useState<string[]>([])
    const [open, setOpen] = useState(false)
    const [showAll, setShowAll] = useState(false)
    const containerRef = useRef<HTMLDivElement>(null)
    const inputRef = useRef<HTMLInputElement>(null)

    useEffect(() => {
        loadDistinctValues(column).then(setOptions)
    }, [column])

    // Close on outside click
    useEffect(() => {
        const handleClick = (e: MouseEvent) => {
            if (containerRef.current && !containerRef.current.contains(e.target as Node)) {
                setOpen(false)
                setShowAll(false)
            }
        }
        document.addEventListener('mousedown', handleClick)
        return () => document.removeEventListener('mousedown', handleClick)
    }, [])

    const visibleOptions = showAll
        ? options
        : value
            ? options.filter(o => o.toLowerCase().includes(value.toLowerCase()))
            : options

    const handleInputChange = (v: string) => {
        onChange(v)
        setShowAll(false)
        setOpen(true)
    }

    const handleArrowClick = (e: React.MouseEvent) => {
        e.preventDefault()
        e.stopPropagation()
        setShowAll(true)
        setOpen(o => !o)
        inputRef.current?.focus()
    }

    const handleSelect = (opt: string) => {
        onChange(opt)
        setOpen(false)
        setShowAll(false)
    }

    // Non-admin: simple select with dynamic options
    if (!isAdmin) {
        return (
            <select
                value={value}
                onChange={e => onChange(e.target.value)}
                autoFocus={autoFocus}
                onKeyDown={onKeyDown}
                className={inputClassName}
            >
                <option value="">—</option>
                {options.map(o => <option key={o} value={o}>{o}</option>)}
            </select>
        )
    }

    return (
        <div ref={containerRef} className="relative flex-1 min-w-0">
            <div className="flex">
                <input
                    ref={inputRef}
                    type="text"
                    value={value}
                    onChange={e => handleInputChange(e.target.value)}
                    onFocus={() => { setShowAll(false); setOpen(true) }}
                    onKeyDown={e => {
                        if (e.key === 'Escape') { setOpen(false); setShowAll(false) }
                        onKeyDown?.(e)
                    }}
                    autoFocus={autoFocus}
                    className={`flex-1 min-w-0 rounded-r-none border-r-0 ${inputClassName}`}
                    placeholder="Sélectionner ou saisir..."
                />
                <button
                    type="button"
                    tabIndex={-1}
                    onMouseDown={handleArrowClick}
                    className="shrink-0 px-1.5 border border-blue-400 rounded-r bg-white hover:bg-gray-50 focus:outline-none"
                >
                    <ChevronDown size={11} className={`text-gray-500 transition-transform ${open ? 'rotate-180' : ''}`} />
                </button>
            </div>

            {open && (
                <div className="absolute z-50 left-0 right-0 mt-0.5 bg-white border border-gray-200 rounded shadow-lg max-h-52 overflow-y-auto">
                    {visibleOptions.length === 0 ? (
                        <p className="text-xs text-gray-400 px-2 py-2 italic">
                            {value ? 'Nouvelle valeur — Entrée pour confirmer' : 'Aucune valeur en base'}
                        </p>
                    ) : (
                        visibleOptions.map(o => (
                            <button
                                key={o}
                                type="button"
                                tabIndex={-1}
                                onMouseDown={e => { e.preventDefault(); handleSelect(o) }}
                                className={`w-full text-left text-xs px-2 py-1.5 hover:bg-blue-50 transition-colors ${
                                    o === value ? 'bg-blue-50 font-semibold text-blue-700' : 'text-gray-700'
                                }`}
                            >
                                {o}
                            </button>
                        ))
                    )}
                </div>
            )}
        </div>
    )
}
